import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Settings from '../Settings';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { payments as paymentsApi } from '@afosto/order/api/Payments';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const PAYMENT_METHOD_PATH = 'afosto.tenant.odr.providers.v1.methods';

const PaymentMethodSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:providers:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const uiSchema = {
    methods: {
      props: {
        label: intl.formatMessage(translations.editDrawer.methodsArrayField.label),
        shouldRemoveId: false,
        prefillId: false,
      },
      code: {
        component: 'PaymentMethodSearch',
        props: {
          label: intl.formatMessage(translations.editDrawer.paymentMethodField.label),
        },
      },
      label: {
        props: {
          label: intl.formatMessage(translations.editDrawer.labelField.label),
        },
      },
      is_enabled: {
        component: 'Checkbox',
        props: {
          label: intl.formatMessage(translations.editDrawer.isEnabledField.label),
        },
      },
      priority: {
        props: {
          label: intl.formatMessage(translations.editDrawer.priorityField.label),
        },
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      const formattedMethods = (fields?.methods ?? []).reduce(
        (acc, method) => [
          ...acc,
          {
            ...method,
            code: method?.code?.code,
            priority: method?.priority ? Number(method?.priority) : 0,
          },
        ],
        [],
      );
      await profileApi.updateProfile(PAYMENT_METHOD_PATH.replace('tenant', pathID), {
        methods: formattedMethods,
      });

      setPaymentMethods(fields);
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentMethodsSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentMethodsSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data: profileData = {} } = await profileApi.getProfile(
          PAYMENT_METHOD_PATH.replace('tenant', pathID),
        );

        const paymentMethodsResponse = await paymentsApi.listPaymentMethods();
        const { data: paymentMethodsData = [] } = paymentMethodsResponse;

        const formattedMethods = (profileData?.methods ?? []).reduce((acc, method) => {
          const currentMethod = findObjectInArray(paymentMethodsData, 'code', method?.code) ?? {};
          const translationKey = currentMethod?.code
            ? `paymentMethods.${currentMethod?.provider_code?.toLowerCase()}.${currentMethod.code}`
            : 'properties.unknownMethod';

          return [
            ...acc,
            {
              ...method,
              code: {
                ...currentMethod,
                label: intl.formatMessage({
                  id: translationKey,
                  defaultMessage: currentMethod?.name ?? 'Unknown method',
                  description: `label of the payment method for code ${currentMethod?.code}`,
                }),
                value: currentMethod?.id,
              },
            },
          ];
        }, []);

        if (isMounted) {
          setPaymentMethods({ methods: formattedMethods });
        }
      } catch (error) {
        if (isMounted) {
          setPaymentMethods(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data = {} } = await profileApi.getProfileDefinitions(
          PAYMENT_METHOD_PATH.replace('tenant', pathID),
        );
        if (isMounted) {
          setFormSchema(data);
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      handleFetchProfile();
      fetchProfileFormSchema();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, intl, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText
              primary={intl.formatMessage(translations.subtitle)}
              secondary={intl.formatMessage(translations.count, {
                count: paymentMethods?.methods?.length ?? 0,
              })}
            />
            <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
              <FormattedMessage {...translations.actions.edit} />
            </Button>
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={paymentMethods}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

PaymentMethodSettings.propTypes = {
  pathID: PropTypes.string,
};

PaymentMethodSettings.defaultProps = {
  pathID: 'tenant',
};

export default PaymentMethodSettings;
